import React, { useState, useEffect, useRef } from 'react';
import { X, User, MapPin, FileText, Loader } from 'lucide-react';
import { useAuth } from '../../Context/AuthContext';
import { upsertUser } from '../../lib/api/user.apis';

interface User {
  uid: string;
  name: string;
  email: string;
  currentCity?: string;
  about?: string;
}

interface EditProfileModalProps {
  user: User;
  userId: string;
  onClose: () => void;
  onUpdate: (updatedUser: User) => void;
}

interface FormData {
  name: string;
  currentCity: string;
  about: string;
}

interface FormErrors {
  name?: string;
  currentCity?: string;
  about?: string;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ user, userId, onClose, onUpdate }) => {
  const [formData, setFormData] = useState<FormData>({
    name: user.name || '',
    currentCity: user.currentCity || '',
    about: user.about || '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const { accessToken } = useAuth();

  // Focus trap and escape key handler
  useEffect(() => {
    // Focus first input
    firstInputRef.current?.focus();

    // Handle escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Handle click outside
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    // Username validation
    if (!formData.name.trim()) {
      newErrors.name = 'Username is required';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Username must be 50 characters or less';
    }

    // Current city validation
    if (!formData.currentCity.trim()) {
      newErrors.currentCity = 'Current city is required';
    } else if (formData.currentCity.length > 50) {
      newErrors.currentCity = 'City must be 50 characters or less';
    }

    // About validation
    if (formData.about.length > 300) {
      newErrors.about = 'About must be 300 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await upsertUser({
        name: formData.name.trim(),
        currentCity: formData.currentCity.trim(),
        about: formData.about.trim(),
        email: user.email,
        token: accessToken
      });
      
      // Update parent component
      onUpdate(response.user);
      
      // Close modal
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to update profile');
      console.error('Error updating profile:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 z-50">
      <div
        ref={modalRef}
        className="bg-white rounded shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-2 border-b border-gray-200">
          <h2 id="modal-title" className="text-xl font-bold text-gray-800">
            Edit Profile
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Submit Error */}
          {submitError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          )}

          {/* Username */}
          <div>
            <label htmlFor="name" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4" />
              Username <span className="text-red-500">*</span>
            </label>
            <input
              ref={firstInputRef}
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              maxLength={50}
              className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 transition-colors ${
                errors.name
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="Enter your username"
            />
            <div className="flex items-center justify-between mt-1">
              {errors.name ? (
                <p className="text-sm text-red-600">{errors.name}</p>
              ) : (
                <div />
              )}
              <p className="text-xs text-gray-500">
                {formData.name.length}/50
              </p>
            </div>
          </div>

          {/* Current City */}
          <div>
            <label htmlFor="currentCity" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4" />
              Current City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="currentCity"
              name="currentCity"
              value={formData.currentCity}
              onChange={handleChange}
              maxLength={50}
              className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 transition-colors ${
                errors.currentCity
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="e.g., New York, Los Angeles"
            />
            <div className="flex items-center justify-between mt-1">
              {errors.currentCity ? (
                <p className="text-sm text-red-600">{errors.currentCity}</p>
              ) : (
                <div />
              )}
              <p className="text-xs text-gray-500">
                {formData.currentCity.length}/50
              </p>
            </div>
          </div>

          {/* About */}
          <div>
            <label htmlFor="about" className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4" />
              About You
            </label>
            <textarea
              id="about"
              name="about"
              value={formData.about}
              onChange={handleChange}
              maxLength={300}
              rows={4}
              className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 resize-none transition-colors ${
                errors.about
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="Write some about yourself..."
            />
            <div className="flex items-center justify-between mt-1">
              {errors.about ? (
                <p className="text-sm text-red-600">{errors.about}</p>
              ) : (
                <div />
              )}
              <p className="text-xs text-gray-500">
                {formData.about.length}/300
              </p>
            </div>
          </div>

          {/* Email (Read-only) */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              Email (Read-only)
            </label>
            <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded text-gray-600">
              {user.email}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Email cannot be changed from this page
            </p>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-[#ee5219] text-white rounded-lg hover:bg-[#d44a16] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;